// src/pages/WidgetPage/ui/StreamWidgetPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import API_CONFIG from '@shared/api/api.config';
import { getFrameType, createAnimator } from '@entities/user/ui/UserAvatar';
import { WidgetUI } from '@features/streamer-widget/ui/StreamerWidgetSettings';
import './StreamWidgetPage.css';

const StreamWidgetPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [localSeconds, setLocalSeconds] = useState(0);
  
  const containerRef = useRef(null);
  const containerAnimRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_CONFIG.API_URL}/website/widgets/stream/${token}/`);
        const newData = response.data;
        setData(newData);
        setError(null);
        if (newData.current_session) setLocalSeconds(newData.current_session.elapsed_seconds || 0);
      } catch (err) {
        setError('WIDGET NOT FOUND OR INACTIVE');
      }
    };
    fetchData();
    const syncInterval = setInterval(fetchData, 10000);
    return () => clearInterval(syncInterval);
  }, [token]);

  useEffect(() => {
    if (!data?.current_session || data.current_session.status !== 'ACTIVE') return;
    const tick = setInterval(() => setLocalSeconds(prev => prev + 1), 1000);
    return () => clearInterval(tick);
  }, [data?.current_session?.status, data?.current_session?.session_id]);

  useEffect(() => {
    if (!data || !data.user) return;
    const isClassic = data.widget_settings.theme === 'classic';
    const selectedFrame = data.widget_settings.frame_type || 'auto';
    const frameType = selectedFrame === 'auto' ? getFrameType(data.user) : selectedFrame;

    const cleanup = () => {
      if (containerAnimRef.current) { cancelAnimationFrame(containerAnimRef.current); containerAnimRef.current = null; }
    };

    if (isClassic && frameType && containerRef.current) {
      createAnimator(containerRef, containerAnimRef)(frameType);
    } else {
      cleanup();
    }
    return cleanup;
  }, [data?.user?.id, data?.widget_settings?.theme, data?.widget_settings?.frame_type]);

  if (error) return <div className="widget-error-screen">{error}</div>;
  if (!data) return null;

  const { widget_settings: settings, challenge, current_session, user } = data;

  const formatTime = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  };

  return (
    <div 
      ref={containerRef}
      className={`stream-widget-container vertical theme-${settings.theme}`}
      style={{
        backgroundColor: settings.colors.background,
        color: settings.colors.text,
        borderColor: settings.theme === 'classic' ? undefined : settings.colors.accent,
        fontFamily: settings.font.family || 'inherit'
      }}
    >
      {!data.has_active_challenge ? (
        <div className="no-challenge-msg">ОЖИДАНИЕ...</div>
      ) : (
        <WidgetUI 
          settings={settings}
          user={user}
          challenge={challenge}
          session={current_session}
          localSeconds={localSeconds}
          formatTime={formatTime}
        />
      )}
    </div>
  );
};

export default StreamWidgetPage;
